import Breadcrumb from '@/components/base/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const services = [
  {
    icon: 'ri-film-line',
    title: '影视聚合浏览',
    desc: '汇聚电影、电视剧、综艺等多元影视资源，分类清晰、一目了然，快速定位想看的内容。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '多维筛选排序',
    desc: '支持按类型、年份、热度与更新时间筛选排序，帮你从海量内容中精准找到心仪之作。',
  },
  {
    icon: 'ri-newspaper-line',
    title: '同步影视资讯',
    desc: '及时同步影视新闻、预告发布与深度专题，追剧同时不错过行业动态。',
  },
  {
    icon: 'ri-magic-line',
    title: '智能内容推荐',
    desc: '基于类型与题材相似度，为你推荐风格相近的作品，让发现好内容变得更简单。',
  },
];

const contacts = [
  { icon: 'ri-mail-line', label: '合作邮箱', value: 'contact@huangniuvideo.com' },
  { icon: 'ri-customer-service-2-line', label: '客服时间', value: '每日 9:00 - 22:00' },
  { icon: 'ri-map-pin-line', label: '办公地点', value: '中国 · 杭州' },
];

const socials = [
  { icon: 'ri-wechat-line', label: '微信公众号' },
  { icon: 'ri-weibo-line', label: '官方微博' },
  { icon: 'ri-bilibili-line', label: '哔哩哔哩' },
];

export default function AboutPage() {
  useDocumentMeta(
    '关于我们 - 黄牛视频',
    '了解黄牛视频：一个高清影视视频资源聚合平台，汇聚电影、电视剧、综艺与影视资讯，致力于让好内容触手可及。',
    '关于黄牛视频,影视聚合平台,联系我们,黄牛视频',
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <div className="relative mb-10 overflow-hidden rounded-lg border border-background-200 bg-gradient-to-br from-background-100 to-background-200/60 p-8 md:p-12">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary-500/10 blur-2xl" />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-500">
            <i className="ri-movie-2-line" />
            关于黄牛视频
          </span>
          <h1 className="mt-4 text-3xl font-black text-foreground-50 md:text-4xl">
            让好内容<span className="text-primary-500">触手可及</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">
            黄牛视频是一个高清影视视频资源聚合平台，面向热爱电影、电视剧与综艺节目的观众，
            提供清晰高效的影视浏览、多维筛选、内容推荐以及即时的影视资讯服务。
            我们希望用简洁大气的方式，让每一次寻找好片都变得轻松愉快。
          </p>
        </div>
      </div>

      <section>
        <SectionTitle title="我们的服务" subtitle="围绕观影需求打造的四大核心能力" icon="ri-service-line" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <div
              key={s.title}
              className="rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-400/60"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
                <i className={`${s.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-50">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <SectionTitle title="联系我们" subtitle="欢迎通过以下方式与我们取得联系" icon="ri-contacts-line" accent="accent" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <ul className="space-y-5">
              {contacts.map((c) => (
                <li key={c.label} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-background-200 text-primary-500">
                    <i className={`${c.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-100">{c.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h3 className="text-base font-semibold text-foreground-50">关注我们</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              关注官方账号，第一时间获取最新影视上架、更新与活动信息。
            </p>
            <div className="mt-6 space-y-3">
              {socials.map((s) => (
                <div
                  key={s.label}
                  className="flex items-center justify-between rounded-md bg-background-200/70 px-4 py-3"
                >
                  <span className="flex items-center gap-3 text-sm text-foreground-100">
                    <i className={`${s.icon} text-lg text-accent-500`} />
                    {s.label}
                  </span>
                  <span className="text-xs text-foreground-500">扫码关注</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <SectionTitle title="版权声明与免责说明" icon="ri-shield-check-line" accent="secondary" />
        <div className="space-y-5 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div>
            <h3 className="text-sm font-semibold text-foreground-100">内容来源说明</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              本站为影视内容聚合演示站点，页面展示的影视作品与资讯均为演示数据，仅用于功能与界面展示，
              不提供任何形式的在线播放源或下载服务。
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-100">免责声明</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              本站不存储、不制作、不传播任何影视资源，如相关权利人对演示内容存在异议，
              可通过上方邮箱与我们联系，我们将及时处理。
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-100">版权归属</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              © 2025 黄牛视频 HUANGNIU VIDEO. 保留所有权利。站内设计、文案及界面结构版权归本站所有，
              未经授权请勿用于商业用途。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}